/** 会话看守：别的标签页换了账号 ⇒ 本页立刻作废（2026-10-11 主人问后补）。
 *
 * 为什么要它：登录态只存 localStorage 一份、同源**全标签页共用**，而每个请求都是
 * 「发出时现读」凭据 —— 也就是说，另一页扫码换了商家之后，本页会
 * **继续显示上一个账号的数据（DOM 里已经渲染好的那些），却把下一个动作写进新账号**：
 * 「看着甲家的货，写进乙家的账」。控制台原先没有任何跨标签页通知
 * （全仓只有一个 storage 监听，在导入页里，且它只等「有没有登录」、不判「是谁」）。
 *
 * 处理口径：**凭据一变就整体重载**，不做局部刷新。菜单、按钮、权限位、已经填了一半的
 * 表单全是按旧身份渲染出来的；只刷新数据会留下「旧账号的按钮 + 新账号的权限」这种更难
 * 看懂、也更容易点错的状态。
 *
 * 两个必须记住的细节：
 * ① `sessionStorage` **能活过 `location.reload()`**，而权限快照 `fit1_mc_me` 就存在里面
 *    ⇒ 重载前必须显式 `McAuth.clear()`，否则「重载了、菜单还是旧账号的」。
 * ② 重载要有闸门：两个标签页各持不同身份时会互相回写 localStorage，没有闸门就会来回刷屏。
 *    浏览器只为**其它**标签页派发 storage 事件，所以本页自己登录/退出不会误触发；
 *    再加一道「本标签页 10 秒内只重载一次」（sessionStorage 按标签页隔离）。
 */
import { LS, clearAccountBuffers } from "../api.js";

const WATCHED = [LS.portal, LS.authMode, LS.key, LS.liveKey, LS.merchantId];

const RELOAD_GUARD_KEY = "fit1_mc_session_watch_reloaded_at";
const RELOAD_GUARD_MS = 10000;

/** 本页认的「我是谁」：门户态看短票+商家，Key 态看 Key。只看身份，不看 base/env 这类设置。 */
function identitySignature() {
  const mode = localStorage.getItem(LS.authMode) || "api_key";
  if (mode === "portal") {
    return `portal|${localStorage.getItem(LS.portal) || ""}|${localStorage.getItem(LS.merchantId) || ""}`;
  }
  return `key|${localStorage.getItem(LS.key) || ""}|${localStorage.getItem(LS.liveKey) || ""}|${localStorage.getItem(LS.merchantId) || ""}`;
}

function merchantId() {
  return localStorage.getItem(LS.merchantId) || "";
}

export function installSessionWatch() {
  let seen = identitySignature();
  let seenMerchant = merchantId();

  window.addEventListener("storage", (ev) => {
    const key = ev && ev.key;
    // ev.key === null 表示 storage.clear()
    if (key && !WATCHED.includes(key)) return;

    const now = identitySignature();
    if (now === seen) {
      seenMerchant = merchantId();
      return;
    }

    // 换的是另一家 ⇒ 随账号走的本地缓冲不能跟着搬
    const mid = merchantId();
    if (seenMerchant && mid && seenMerchant !== mid) clearAccountBuffers();
    seen = now;
    seenMerchant = mid;

    if (window.McAuth && window.McAuth.clear) window.McAuth.clear();
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last < RELOAD_GUARD_MS) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      /* sessionStorage 不可用时照旧重载（只是少一道闸门） */
    }
    window.location.reload();
  });
}
